package dev.danko.roblox_craft_ai.uikit.components

import androidx.compose.runtime.Composable
import com.varabyte.kobweb.compose.css.Cursor
import com.varabyte.kobweb.compose.css.FontWeight
import com.varabyte.kobweb.compose.foundation.layout.Box
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.foundation.layout.Row
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.silk.components.icons.fa.FaIcon
import com.varabyte.kobweb.silk.components.text.SpanText
import dev.danko.roblox_craft_ai.uikit.theme.LocalAppTheme
import org.jetbrains.compose.web.css.LineStyle
import org.jetbrains.compose.web.css.cssRem
import org.jetbrains.compose.web.css.px

enum class AlertVariant {
    INFO,
    SUCCESS,
    WARNING,
    DANGER
}

@Composable
fun AppAlert(
    message: String,
    modifier: Modifier = Modifier,
    title: String? = null,
    variant: AlertVariant = AlertVariant.INFO,
    onDismiss: (() -> Unit)? = null,
    action: (@Composable () -> Unit)? = null
) {
    val theme = LocalAppTheme.current
    val palette = theme.colors

    val (accentColor, bgColor) = when (variant) {
        AlertVariant.INFO -> palette.statusProcessing to palette.statusProcessing.toRgb().copyf(alpha = 0.12f)
        AlertVariant.SUCCESS -> palette.statusReady to palette.statusReady.toRgb().copyf(alpha = 0.12f)
        AlertVariant.WARNING -> palette.statusPending to palette.statusPending.toRgb().copyf(alpha = 0.12f)
        AlertVariant.DANGER -> palette.statusFailed to palette.statusFailed.toRgb().copyf(alpha = 0.12f)
    }

    Row(
        modifier = Modifier
            .fillMaxWidth()
            .backgroundColor(bgColor)
            .borderRadius(theme.radii.md)
            .border(width = 1.px, style = LineStyle.Solid, color = accentColor.toRgb().copyf(alpha = 0.35f))
            .borderLeft(width = 4.px, style = LineStyle.Solid, color = accentColor)
            .padding(all = 0.75.cssRem)
            .then(modifier),
        verticalAlignment = Alignment.Top
    ) {
        Box(modifier = Modifier.margin(right = 0.65.cssRem, top = 2.px)) {
            when (variant) {
                AlertVariant.INFO -> FaIcon("circle-info", modifier = Modifier.color(accentColor).fontSize(1.1.cssRem))
                AlertVariant.SUCCESS -> FaIcon("circle-check", modifier = Modifier.color(accentColor).fontSize(1.1.cssRem))
                AlertVariant.WARNING -> FaIcon("triangle-exclamation", modifier = Modifier.color(accentColor).fontSize(1.1.cssRem))
                AlertVariant.DANGER -> FaIcon("circle-exclamation", modifier = Modifier.color(accentColor).fontSize(1.1.cssRem))
            }
        }

        Column(modifier = Modifier.weight(1f)) {
            if (!title.isNullOrBlank()) {
                SpanText(
                    text = title,
                    modifier = Modifier
                        .fontSize(0.9.cssRem)
                        .fontWeight(FontWeight.Bold)
                        .color(palette.textPrimary)
                        .margin(bottom = 0.2.cssRem)
                )
            }
            SpanText(
                text = message,
                modifier = Modifier
                    .fontSize(0.85.cssRem)
                    .color(palette.textPrimary)
                    .lineHeight(1.35)
            )

            if (action != null) {
                Box(modifier = Modifier.margin(top = 0.5.cssRem)) {
                    action()
                }
            }
        }

        if (onDismiss != null) {
            Box(
                modifier = Modifier
                    .margin(left = 0.5.cssRem)
                    .cursor(Cursor.Pointer)
                    .onClick { onDismiss() }
                    .opacity(0.7)
            ) {
                FaIcon("xmark", modifier = Modifier.color(palette.textSecondary).fontSize(0.9.cssRem))
            }
        }
    }
}
